/**
 * Upload Settings Admin Panel
 *
 * Admin panel for configuring file upload limits and compression settings
 */

import { useState } from 'react';
import { useSystemSettings, useUpdateSettings } from '@/hooks/useUploadSettings';

export const UploadSettingsPanel = () => {
  const { data: allSettings, isLoading, error } = useSystemSettings('upload');
  const updateSettings = useUpdateSettings();

  const [formData, setFormData] = useState({
    max_file_size_mb: 500,
    max_voice_message_size_mb: 10,
    max_files_per_upload: 10,
    enable_file_compression: true,
  });

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');

  // Initialize form data from settings (adjust state during render on change)
  const [syncedSettings, setSyncedSettings] = useState(allSettings);
  if (allSettings && allSettings !== syncedSettings) {
    setSyncedSettings(allSettings);
    setFormData({
      max_file_size_mb: (allSettings.max_file_size_mb?.value as number) || 500,
      max_voice_message_size_mb: (allSettings.max_voice_message_size_mb?.value as number) || 10,
      max_files_per_upload: (allSettings.max_files_per_upload?.value as number) || 10,
      enable_file_compression: (allSettings.enable_file_compression?.value as boolean) ?? true,
    });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveStatus('saving');

    try {
      await updateSettings.mutateAsync(formData);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to update settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-gray-600">Зареждане на настройките...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 font-medium">Грешка при зареждане на настройките</p>
          <p className="text-red-600 text-sm mt-1">Моля, опитайте отново по-късно.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Настройки за качване на файлове</h2>
        <p className="text-gray-600 mt-1">
          Конфигурирайте лимитите за размер на файлове и поведението при качване
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Regular File Size Limit */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Максимален размер на файл (MB)
          </label>
          <input
            type="number"
            min="1"
            max="10000"
            value={formData.max_file_size_mb}
            onChange={(e) => handleChange('max_file_size_mb', parseInt(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">
            Максимален размер за стандартни файлове (изображения, видео, документи)
          </p>
        </div>

        {/* Voice Message Size Limit */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Максимален размер на гласово съобщение (MB)
          </label>
          <input
            type="number"
            min="1"
            max="100"
            value={formData.max_voice_message_size_mb}
            onChange={(e) => handleChange('max_voice_message_size_mb', parseInt(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">
            Максимален размер за аудио/гласови съобщения
          </p>
        </div>

        {/* Max Files Per Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Максимален брой файлове на качване
          </label>
          <input
            type="number"
            min="1"
            max="50"
            value={formData.max_files_per_upload}
            onChange={(e) => handleChange('max_files_per_upload', parseInt(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">
            Максимален брой файлове, които могат да се качат едновременно
          </p>
        </div>

        {/* Enable File Compression */}
        <div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enable_file_compression}
              onChange={(e) => handleChange('enable_file_compression', e.target.checked)}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="ml-3 text-sm font-medium text-gray-700">
              Активирай автоматична компресия на файлове
            </span>
          </label>
          <p className="text-sm text-gray-500 mt-1 ml-8">
            Автоматично компресира изображения и видеа при качване за икономия на място
          </p>
        </div>

        {/* Current Values Summary */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Текущи стойности</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Стандартни файлове:</span>
              <span className="ml-2 font-medium text-gray-900">
                {formData.max_file_size_mb} MB
              </span>
            </div>
            <div>
              <span className="text-gray-600">Гласови съобщения:</span>
              <span className="ml-2 font-medium text-gray-900">
                {formData.max_voice_message_size_mb} MB
              </span>
            </div>
            <div>
              <span className="text-gray-600">Макс. файлове:</span>
              <span className="ml-2 font-medium text-gray-900">
                {formData.max_files_per_upload}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Компресия:</span>
              <span className="ml-2 font-medium text-gray-900">
                {formData.enable_file_compression ? 'Активна' : 'Неактивна'}
              </span>
            </div>
          </div>
        </div>

        {/* Save Button and Status */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex-1">
            {saveStatus === 'success' && (
              <div className="flex items-center text-green-600">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">Настройките са запазени успешно</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className="flex items-center text-red-600">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">Грешка при запазване на настройките</span>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={saveStatus === 'saving'}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saveStatus === 'saving' ? (
              <span className="flex items-center">
                <svg className="w-5 h-5 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Запазване...
              </span>
            ) : (
              'Запази настройки'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
